import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Alert,
  InputGroup,
  Badge,
  Spinner
} from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaUserFriends, FaUniversity } from 'react-icons/fa';
import Layout from '../../components/Layout';
import api from '../../services/api/depositosApi';

const Beneficiarios = () => {
  // Estados principales
  const [beneficiarios, setBeneficiarios] = useState([]);
  const [tiposCuentaBancaria, setTiposCuentaBancaria] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Estados de filtros
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroBusqueda, setFiltroBusqueda] = useState('');

  // Estados del modal
  const [showModal, setShowModal] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [beneficiarioActual, setBeneficiarioActual] = useState(null);
  const [mostrarDatosContacto, setMostrarDatosContacto] = useState(false);

  // Estado del formulario
  const [formData, setFormData] = useState({
    tipo: 'proveedor',
    nombre: '',
    numero_documento: '',
    contacto_nombre: '',
    contacto_email: '',
    contacto_telefono: '',
    direccion: ''
  });

  // Estados para cuentas bancarias
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [cuentaTemporal, setCuentaTemporal] = useState({
    tipo_cuenta_bancaria_id: '',
    banco: '',
    numero_cuenta: '',
    codigo_cuenta: '',
    moneda: 'PEN',
    es_principal: false
  });

  // Estados de confirmación
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [beneficiarioAEliminar, setBeneficiarioAEliminar] = useState(null);

  // Cargar datos iniciales
  useEffect(() => {
    loadBeneficiarios();
    loadTiposCuentaBancaria();
  }, []);

  const loadBeneficiarios = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.getBeneficiarios();
      if (response.data) {
        setBeneficiarios(response.data);
      }
    } catch (err) {
      setError('Error al cargar beneficiarios: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadTiposCuentaBancaria = async () => {
    try {
      const response = await api.getTiposCuentaBancaria();
      if (response.data) {
        setTiposCuentaBancaria(response.data);
      }
    } catch (err) {
      console.error('Error al cargar tipos de cuenta bancaria:', err);
    }
  };

  // Handlers del formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCuentaTemporalInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCuentaTemporal(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const agregarCuentaBancaria = () => {
    if (!cuentaTemporal.tipo_cuenta_bancaria_id || !cuentaTemporal.banco || !cuentaTemporal.numero_cuenta) {
      setError('Complete los campos obligatorios de la cuenta bancaria');
      return;
    }

    // Verificar si ya existe una cuenta principal
    const yaExistePrincipal = cuentasBancarias.some(c => c.es_principal);
    if (cuentaTemporal.es_principal && yaExistePrincipal) {
      setError('Ya existe una cuenta marcada como principal');
      return;
    }

    setCuentasBancarias(prev => [...prev, { ...cuentaTemporal }]);
    
    // Limpiar formulario temporal
    setCuentaTemporal({
      tipo_cuenta_bancaria_id: '',
      banco: '',
      numero_cuenta: '',
      codigo_cuenta: '',
      moneda: 'PEN',
      es_principal: false
    });
    setError('');
  };

  const eliminarCuentaBancaria = (index) => {
    setCuentasBancarias(prev => prev.filter((_, i) => i !== index));
  };

  const handleNuevo = () => {
    setModoEdicion(false);
    setBeneficiarioActual(null);
    setFormData({
      tipo: 'proveedor',
      nombre: '',
      numero_documento: '',
      contacto_nombre: '',
      contacto_email: '',
      contacto_telefono: '',
      direccion: ''
    });
    setCuentasBancarias([]);
    setMostrarDatosContacto(false);
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleEditar = (beneficiario) => {
    setModoEdicion(true);
    setBeneficiarioActual(beneficiario);
    setFormData({
      tipo: beneficiario.tipo,
      nombre: beneficiario.nombre,
      numero_documento: beneficiario.numero_documento || '',
      contacto_nombre: beneficiario.contacto_nombre || '',
      contacto_email: beneficiario.contacto_email || '',
      contacto_telefono: beneficiario.contacto_telefono || '',
      direccion: beneficiario.direccion || ''
    });
    
    // Verificar si tiene datos de contacto
    const tieneDatosContacto = beneficiario.contacto_nombre || beneficiario.contacto_email || beneficiario.contacto_telefono;
    setMostrarDatosContacto(tieneDatosContacto);
    
    // Cargar cuentas bancarias existentes
    loadCuentasBeneficiario(beneficiario.id);
    
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const loadCuentasBeneficiario = async (beneficiarioId) => {
    try {
      const response = await api.getCuentasByBeneficiario(beneficiarioId);
      if (response.data) {
        setCuentasBancarias(response.data);
      }
    } catch (err) {
      console.error('Error al cargar cuentas:', err);
    }
  };

  const handleEliminar = (beneficiario) => {
    setBeneficiarioAEliminar(beneficiario);
    setShowConfirmDelete(true);
  };

  const confirmarEliminacion = async () => {
    if (!beneficiarioAEliminar) return;

    try {
      setLoading(true);
      await api.deleteBeneficiario(beneficiarioAEliminar.id);
      setSuccess(`Beneficiario "${beneficiarioAEliminar.nombre}" eliminado correctamente`);
      loadBeneficiarios();
      setShowConfirmDelete(false);
      setBeneficiarioAEliminar(null);
    } catch (err) {
      setError('Error al eliminar: ' + (err.response?.data?.detail || err.message));
      setShowConfirmDelete(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      setLoading(true);

      const dataToSend = {
        ...formData,
        cuentas: cuentasBancarias
      };

      if (modoEdicion) {
        // Actualizar beneficiario existente
        await api.updateBeneficiario(beneficiarioActual.id, formData);
        
        // Aquí podrías actualizar las cuentas bancarias también
        // Por ahora, solo actualizamos el beneficiario
        
        setSuccess('Beneficiario actualizado correctamente');
      } else {
        // Crear nuevo beneficiario con cuentas
        await api.createBeneficiarioConCuentas(dataToSend);
        setSuccess('Beneficiario creado correctamente');
      }

      loadBeneficiarios();
      setShowModal(false);
    } catch (err) {
      setError('Error al guardar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Filtrar beneficiarios
  const beneficiariosFiltrados = beneficiarios.filter(ben => {
    const coincideTipo = !filtroTipo || ben.tipo === filtroTipo;
    const coincideBusqueda = !filtroBusqueda || 
      ben.nombre?.toLowerCase().includes(filtroBusqueda.toLowerCase()) ||
      ben.numero_documento?.toLowerCase().includes(filtroBusqueda.toLowerCase()) ||
      ben.codigo?.toLowerCase().includes(filtroBusqueda.toLowerCase());
    
    return coincideTipo && coincideBusqueda;
  });

  const getTipoBadge = (tipo) => {
    const badges = {
      proveedor: { bg: 'primary', text: 'Proveedor' },
      beneficiario: { bg: 'success', text: 'Beneficiario' },
      servicio: { bg: 'info', text: 'Servicio' }
    };
    const badge = badges[tipo] || { bg: 'secondary', text: tipo };
    return <Badge bg={badge.bg}>{badge.text}</Badge>;
  };

  const getTipoCuentaNombre = (tipoId) => {
    const tipo = tiposCuentaBancaria.find(t => t.id === parseInt(tipoId));
    return tipo ? tipo.nombre : tipoId;
  };

  return (
    <Layout>
      <Container fluid className="py-4">
        {/* Encabezado */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-1">
                  <FaUserFriends className="me-2" />
                  Gestión de Beneficiarios, Proveedores y Servicios
                </h2>
                <p className="text-muted mb-0">
                  Administre los beneficiarios, proveedores y servicios del sistema
                </p>
              </div>
              <Button variant="primary" size="lg" onClick={handleNuevo}>
                <FaPlus className="me-2" />
                Nuevo Registro
              </Button>
            </div>
          </Col>
        </Row>

        {/* Mensajes */}
        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Filtros */}
        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <Row className="g-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Tipo</Form.Label>
                  <Form.Select
                    value={filtroTipo}
                    onChange={(e) => setFiltroTipo(e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="proveedor">Proveedor</option>
                    <option value="beneficiario">Beneficiario</option>
                    <option value="servicio">Servicio</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Buscar</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaSearch /></InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Buscar por nombre, documento o código..."
                      value={filtroBusqueda}
                      onChange={(e) => setFiltroBusqueda(e.target.value)}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <div className="text-muted">
                  <strong>{beneficiariosFiltrados.length}</strong> registro(s) encontrado(s)
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tabla */}
        <Card className="shadow-sm">
          <Card.Body>
            {loading && beneficiarios.length === 0 ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Cargando...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover>
                  <thead className="table-light">
                    <tr>
                      <th>Código</th>
                      <th>Tipo</th>
                      <th>Nombre / Razón Social</th>
                      <th>Documento</th>
                      <th>Contacto</th>
                      <th className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {beneficiariosFiltrados.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center text-muted py-4">
                          No se encontraron registros
                        </td>
                      </tr>
                    ) : (
                      beneficiariosFiltrados.map(ben => (
                        <tr key={ben.id}>
                          <td>
                            <code>{ben.codigo}</code>
                          </td>
                          <td>{getTipoBadge(ben.tipo)}</td>
                          <td>
                            <strong>{ben.nombre}</strong>
                          </td>
                          <td>{ben.numero_documento || '-'}</td>
                          <td>
                            {ben.contacto_nombre ? (
                              <div>
                                <div className="small">{ben.contacto_nombre}</div>
                                {ben.contacto_telefono && (
                                  <div className="small text-muted">{ben.contacto_telefono}</div>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-2"
                              onClick={() => handleEditar(ben)}
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleEliminar(ben)}
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Modal de Crear/Editar */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>
              {modoEdicion ? 'Editar' : 'Nuevo'} {formData.tipo === 'proveedor' ? 'Proveedor' : formData.tipo === 'servicio' ? 'Servicio' : 'Beneficiario'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              {error && <Alert variant="danger">{error}</Alert>}

              <Row className="g-3">
                {/* Tipo */}
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Tipo <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="tipo"
                      value={formData.tipo}
                      onChange={handleInputChange}
                      required
                      disabled={modoEdicion}
                    >
                      <option value="proveedor">Proveedor</option>
                      <option value="beneficiario">Beneficiario</option>
                      <option value="servicio">Servicio</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                {/* Nombre */}
                <Col md={9}>
                  <Form.Group>
                    <Form.Label>Nombre / Razón Social <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleInputChange}
                      placeholder="Ingrese el nombre o razón social"
                      required
                    />
                  </Form.Group>
                </Col>

                {/* Número de Documento */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Número de Documento (RUC/DNI)</Form.Label>
                    <Form.Control
                      type="text"
                      name="numero_documento"
                      value={formData.numero_documento}
                      onChange={handleInputChange}
                      placeholder="Ingrese RUC o DNI"
                    />
                  </Form.Group>
                </Col>

                {/* Dirección */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Dirección</Form.Label>
                    <Form.Control
                      type="text"
                      name="direccion"
                      value={formData.direccion}
                      onChange={handleInputChange}
                      placeholder="Ingrese la dirección"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <hr className="my-4" />

              {/* Cuentas Bancarias */}
              <div className="mb-3">
                <h5 className="mb-3">
                  <FaUniversity className="me-2" />
                  Cuentas Bancarias
                </h5>
                
                <Card className="mb-3">
                  <Card.Body>
                    <Row className="g-3">
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Tipo de Cuenta</Form.Label>
                          <Form.Select
                            name="tipo_cuenta_bancaria_id"
                            value={cuentaTemporal.tipo_cuenta_bancaria_id}
                            onChange={handleCuentaTemporalInputChange}
                          >
                            <option value="">Seleccione...</option>
                            {tiposCuentaBancaria.map(tipo => (
                              <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Banco</Form.Label>
                          <Form.Control
                            type="text"
                            name="banco"
                            value={cuentaTemporal.banco}
                            onChange={handleCuentaTemporalInputChange}
                            placeholder="Nombre del banco"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Número de Cuenta</Form.Label>
                          <Form.Control
                            type="text"
                            name="numero_cuenta"
                            value={cuentaTemporal.numero_cuenta}
                            onChange={handleCuentaTemporalInputChange}
                            placeholder="Número de cuenta"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>Código (CCI)</Form.Label>
                          <Form.Control
                            type="text"
                            name="codigo_cuenta"
                            value={cuentaTemporal.codigo_cuenta}
                            onChange={handleCuentaTemporalInputChange}
                            placeholder="Código CCI"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={1} className="d-flex align-items-end">
                        <Button variant="primary" onClick={agregarCuentaBancaria} className="w-100">
                          <FaPlus />
                        </Button>
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>Moneda</Form.Label>
                          <Form.Select
                            name="moneda"
                            value={cuentaTemporal.moneda}
                            onChange={handleCuentaTemporalInputChange}
                          >
                            <option value="PEN">PEN (Soles)</option>
                            <option value="USD">USD (Dólares)</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3} className="d-flex align-items-end">
                        <Form.Check
                          type="checkbox"
                          name="es_principal"
                          label="Marcar como cuenta principal"
                          checked={cuentaTemporal.es_principal}
                          onChange={handleCuentaTemporalInputChange}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {cuentasBancarias.length > 0 && (
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Banco</th>
                        <th>Número de Cuenta</th>
                        <th>Código</th>
                        <th>Moneda</th>
                        <th>Principal</th>
                        <th className="text-center">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cuentasBancarias.map((cuenta, index) => (
                        <tr key={index}>
                          <td>{getTipoCuentaNombre(cuenta.tipo_cuenta_bancaria_id)}</td>
                          <td>{cuenta.banco}</td>
                          <td>{cuenta.numero_cuenta}</td>
                          <td>{cuenta.codigo_cuenta || '-'}</td>
                          <td>{cuenta.moneda}</td>
                          <td>
                            {cuenta.es_principal && <Badge bg="success">Sí</Badge>}
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => eliminarCuentaBancaria(index)}
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>

              <hr className="my-4" />

              {/* Datos de Contacto (opcional) */}
              <Form.Check
                type="checkbox"
                label="¿Desea ingresar datos de contacto?"
                checked={mostrarDatosContacto}
                onChange={(e) => setMostrarDatosContacto(e.target.checked)}
                className="mb-3"
              />

              {mostrarDatosContacto && (
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Nombre del Contacto</Form.Label>
                      <Form.Control
                        type="text"
                        name="contacto_nombre"
                        value={formData.contacto_nombre}
                        onChange={handleInputChange}
                        placeholder="Nombre del contacto"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Email del Contacto</Form.Label>
                      <Form.Control
                        type="email"
                        name="contacto_email"
                        value={formData.contacto_email}
                        onChange={handleInputChange}
                        placeholder="email@ejemplo.com"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Teléfono del Contacto</Form.Label>
                      <Form.Control
                        type="text"
                        name="contacto_telefono"
                        value={formData.contacto_telefono}
                        onChange={handleInputChange}
                        placeholder="Teléfono"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)} disabled={loading}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? <Spinner animation="border" size="sm" /> : (modoEdicion ? 'Actualizar' : 'Crear')}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Modal de Confirmación de Eliminación */}
        <Modal show={showConfirmDelete} onHide={() => setShowConfirmDelete(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirmar Eliminación</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {beneficiarioAEliminar && (
              <p>
                ¿Está seguro de que desea eliminar el {beneficiarioAEliminar.tipo}{' '}
                <strong>"{beneficiarioAEliminar.nombre}"</strong>?
              </p>
            )}
            <Alert variant="warning">
              <strong>Nota:</strong> Solo se puede eliminar si no tiene depósitos asociados. 
              De lo contrario, será desactivado.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfirmDelete(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmarEliminacion} disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : 'Eliminar'}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Layout>
  );
};

export default Beneficiarios;
